import {
  createRouter,
  createWebHistory,
  NavigationGuard,
  NavigationHookAfter,
  Router,
} from "vue-router";
import { useAuthStore } from "@/store/auth.ts";
import { useClientConfig } from "@/store/client-config";
import { trackPageview } from "@/utils/tracking.ts";
import { routes, handleHotUpdate } from "vue-router/auto-routes";
import { userHasPermission } from "@/composables/user-permissions.ts";
import { useLogger } from "@/services/logger";
import { AuthenticationService } from "@/services/authentication";
import { useAppStore } from "@/store/app";

const logger = useLogger();

const beforeGuard: NavigationGuard = async (to, _from, next) => {
  const authStore = useAuthStore();
  const appStore = useAppStore();
  const clientConfig = useClientConfig();
  const isPublicRoute: boolean = to.meta.public === true;
  const isScopedRoute: boolean = !!to.meta.scope;

  logger.trace(`Route: ${to.fullPath}`, { isPublicRoute, isScopedRoute, isAuthenticated: authStore.isAuthenticated });

  if (!isPublicRoute) {
    // Run HandleRedirect here. It's not a good name from MSAL since you'd think you need to run it on redirect pages. Their doc specifies you need to run it on every non-public pages.
    const isAuthed = await AuthenticationService.isAuthenticated();
    if (!isAuthed) {
      logger.trace('Route: Going to /auth/login');
      return next(`/auth/login`);
    }
    const wasAuthenticated = authStore.isAuthenticated;
    authStore.setUser(AuthenticationService.getActiveAccount());

    if (!wasAuthenticated && authStore.isAuthenticated) {
      await appStore.start();
    }

    if (authStore.isAuthenticated) {
      if (to.name === 'auth-callback') {
        return next('/scorecards');
      }
      if (to.path !== "/select" &&
        clientConfig.selectedTenant === -1) {
        logger.trace('Route: No tenant selected, going to /select');
        return next("/select");
      }
      if (isScopedRoute) {
        await authStore.fetchActiveUser();
        if (!userHasPermission(to.meta.scope!)) {
          return next("/dashboard?unauthorized");
        }
      }
    }
  } else {
    if (authStore.isAuthenticated) {
      logger.trace('Route: Trying to access public route as authenticated user, going to /dashboard');
      return next("/scorecards");
    }
  }

  // Block mockup routes in production unless explicitly enabled
  // disabled for now
  // if (
  //   to.path.startsWith("/mockups") &&
  //   !import.meta.env.DEV &&
  //   !import.meta.env.VITE_ENABLE_MOCKUPS
  // ) {
  //   return next("/");
  // }

  logger.trace('Route: Proceeding');

  next();
};

const afterGuard: NavigationHookAfter = (to, from): void => {
  trackPageview({
    path: to.fullPath,
    name: to.path,
    properties: {
      pageName: to.path,
      pagePath: to.fullPath,
      previousPage: {
        fullPath: from.fullPath,
        name: from.name,
      },
      version: 0.1,
    },
  });
};

export const router: Router = createRouter({
  history: createWebHistory(),
  routes,
});

// This will update routes at runtime without reloading the page
if (import.meta.hot) {
  handleHotUpdate(router);
}

router.beforeEach(beforeGuard);
router.afterEach(afterGuard);
